import * as React from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FilterDef, FilterValue } from './types';
import { isFilterEmpty } from './useTableFilters';
import FilterControl from './FilterControl';
import AddFilterPopover from './AddFilterPopover';
import SearchInput from './SearchInput';

interface DataTableToolbarProps {
  filters: FilterDef[];
  values: Record<string, FilterValue>;
  onChange: (id: string, value: FilterValue) => void;
  onClear: (id: string) => void;
  onClearAll: () => void;
  search?: {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
  };
  /** Extra controls rendered on the right (e.g. sort order). */
  rightSlot?: React.ReactNode;
  className?: string;
}

/**
 * Shared filter toolbar: search box, pinned quick-filter buttons, a Linear-style
 * "+ Filter" menu, and removable chips for the rest. Fully driven by the
 * per-table filter registry passed in `filters`.
 */
const DataTableToolbar: React.FC<DataTableToolbarProps> = ({
  filters,
  values,
  onChange,
  onClear,
  onClearAll,
  search,
  rightSlot,
  className,
}) => {
  const pinned = React.useMemo(() => filters.filter((f) => f.pinned), [filters]);
  const nonPinned = React.useMemo(() => filters.filter((f) => !f.pinned), [filters]);

  const isActive = React.useCallback(
    (def: FilterDef) => !isFilterEmpty(def, values[def.id]),
    [values],
  );

  // Non-pinned filters that the user added but hasn't given a value yet.
  const [manualIds, setManualIds] = React.useState<string[]>([]);
  const [justAdded, setJustAdded] = React.useState<string | null>(null);

  const shownNonPinned = React.useMemo(() => {
    const ids = new Set<string>(manualIds);
    nonPinned.forEach((def) => {
      if (isActive(def)) ids.add(def.id);
    });
    return nonPinned.filter((def) => ids.has(def.id));
  }, [nonPinned, manualIds, isActive]);

  const available = React.useMemo(
    () => nonPinned.filter((def) => !shownNonPinned.includes(def)),
    [nonPinned, shownNonPinned],
  );

  const handlePick = (def: FilterDef) => {
    setManualIds((ids) => (ids.includes(def.id) ? ids : [...ids, def.id]));
    setJustAdded(def.id);
  };

  const handleRemove = (def: FilterDef) => {
    setManualIds((ids) => ids.filter((id) => id !== def.id));
    if (justAdded === def.id) setJustAdded(null);
    onClear(def.id);
  };

  const anyActive = filters.some(isActive) || !!search?.value;

  const clearEverything = () => {
    setManualIds([]);
    setJustAdded(null);
    // onClearAll clears the registered filters AND the search param in a single
    // URL write, so we must not fire a second navigate here.
    onClearAll();
  };

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className || ''}`}>
      {search && (
        <SearchInput
          value={search.value}
          onChange={search.onChange}
          placeholder={search.placeholder}
          className="w-full sm:w-64"
        />
      )}

      {pinned.map((def) => (
        <FilterControl
          key={def.id}
          def={def}
          value={values[def.id]}
          onChange={(v) => onChange(def.id, v)}
        />
      ))}

      {shownNonPinned.map((def) => (
        <div key={def.id} className="flex items-center">
          <FilterControl
            def={def}
            value={values[def.id]}
            onChange={(v) => onChange(def.id, v)}
            defaultOpen={justAdded === def.id}
          />
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-7 text-muted-foreground hover:text-destructive"
            onClick={() => handleRemove(def)}
            aria-label={`Remove ${def.label} filter`}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      ))}

      <AddFilterPopover available={available} onPick={handlePick} />

      {anyActive && (
        <Button
          variant="ghost"
          className="h-9 text-sm text-muted-foreground"
          onClick={clearEverything}
        >
          Clear all
        </Button>
      )}

      {rightSlot ? <div className="ml-auto flex items-center gap-2">{rightSlot}</div> : null}
    </div>
  );
};

export default DataTableToolbar;
